import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';

import {
  EmptyState,
  FilterButton,
  LimitPaginationButton,
  Loading,
  PageTitle,
  PaginationGroup,
  RadioButton,
  SaveFilterBlock,
  TableColumnSetting,
  TableTitle,
  Text
} from 'components/common/Elements/Elements';

import { modalActionBody } from 'components/common/Elements/NewModal/newModal';
import FeatureInfo from './parts/FeatureInfo/FeatureInfo';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import PageTable from './parts/PageTable/PageTable';
import ResultAlert from './parts/ResultAlert/ResultAlert';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';
import FooterModalCustomers from './parts/FooterModalCustomers/FooterModalCustomers';
import { MAX_TENANT_IDS } from './parts/TenantIdsAutocomplete/TenantIdsAutocomplete';
import {
  mainTableConf,
  defaultFilters,
  defaultNavigation
} from './helpers/configurates';
import { getRequest, putRequest, deleteRequest } from 'utils/requests';
import {
  replaceHistory,
  paddingForMenuSettings,
  checkingForExistenceOfOptions,
  parsConfigForHistory,
  reParsConfigForHistory,
  clearOrDefaultFilters,
  limitCustomersSelect
} from 'utils/common';
import { showNotif, hideNotif } from '@p81-common/ui';
import { createReqBody } from './helpers/helpers';
import { validationNumber } from 'utils/validation';

import classes from './AssignedCustomers.module.scss';

// Mounted by AssignedCustomersRoute once the flag is resolved in the current
// region, so every request here uses that region's `_id`.
const AssignedCustomers = ({ dataSelectCell, isNotAssignedCustomers }) => {
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(
    dataSelectCell?.autoAssign ?? false
  );
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [pastedTenantIds, setPastedTenantIds] = useState([]);
  //   filters and pagination
  const [filter, setFilter] = useState({
    ...defaultFilters,
    ...defaultNavigation
  });

  const [filterBackend, setFilterBackend] = useState({
    key: 'flagCustomers',
    value: []
  });
  const [visibleFilterSave, setVisibleFilterSave] = useState(false);
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [trigerResetDatePicker, setTrigerResetDatePicker] = useState(false);

  const [filterButton, setFilterButton] = useState(false);
  const [tableConf, setTableConf] = useState(
    JSON.parse(JSON.stringify(mainTableConf))
  );

  const units = ['Not Assigned', 'Assigned'];

  //data from api
  const [data, setData] = useState({});
  const [filters, setFilters] = useState({});

  //other data
  const [loader, setLoader] = useState(true);
  const [loaderTable, setLoaderTable] = useState(true);
  const [dataConection, setDataConection] = useState(false);
  const [selectLoader, setSelectLoader] = useState(false);

  // if you touch this trigger, then a request for filter lists is sent
  const [startFilter, setStartFilter] = useState(false);
  // status for comparison whether new options have been selected
  const [prevStateFilter, setPrevStateFilter] = useState();
  const [prevStateTableConf, setPrevStateTableConf] = useState();

  const [triggerSaveTableSettings, setTriggerSaveTableSettings] =
    useState(false);
  const [triggerStartEffect, setTriggerStartEffect] = useState(false);
  // const [enabledRequest, setEnabledRequest] = useState(true);

  const { t } = useTranslation(['activity']);

  const history = useHistory();
  const query = new URLSearchParams(history.location.search);

  const tableHeight = useRef();
  const unmount = useRef(false);
  const mismatchWarningShown = useRef(false);

  const isTenantIdFilterActive = pastedTenantIds.length > 0;

  const handleUnit = e => {
    if (e.target.value !== filter.onlyAssigned) {
      setSelectedCustomers([]);
      setFilter({
        ...filter,
        onlyAssigned: e.target.value === 'Assigned' ? true : false
      });
      setStartFilter(!startFilter);
    }
  };

  useEffect(() => {
    if (dataSelectCell && triggerStartEffect) {
      mismatchWarningShown.current = false;
      setSelectedCustomers([]);
      if (pastedTenantIds.length > MAX_TENANT_IDS) {
        return;
      }
      if (pastedTenantIds.length > 0) {
        const resetFilter = {
          ...defaultFilters,
          ...defaultNavigation,
          onlyAssigned: filter.onlyAssigned,
          currentPage: 1
        };
        setFilter(resetFilter);
        setVisibleFilterSave(false);
        setVisibleFilterEdit(false);
        requestForFilteredData(resetFilter);
      } else {
        setFilter(prev => ({ ...prev, currentPage: 1 }));
        requestForFilteredData({ ...filter, currentPage: 1 });
      }
    }
    // eslint-disable-next-line
  }, [
    pastedTenantIds,
    dataSelectCell,
    triggerStartEffect,
    filter.onlyAssigned
  ]);

  useEffect(() => {
    if (selectedCustomers.length === limitCustomersSelect) {
      showNotif(
        t('activity:featureFlag.CUSTOMERS_LIMIT_MESSAGE', {
          limit: limitCustomersSelect
        }),
        { alertProps: { severity: 'error' } }
      );
    }
  }, [selectedCustomers, t]);

  const handleCheckCustomer = company => {
    if (
      !selectedCustomers.find(item => item.customerId === company.customerId)
    ) {
      setSelectedCustomers([...selectedCustomers, company]);
    } else {
      setSelectedCustomers(
        selectedCustomers.filter(item => item.customerId !== company.customerId)
      );
    }
  };

  const isSelectedCustomer = customerId => {
    return selectedCustomers.find(item => item.customerId === customerId);
  };

  const handleChekBox = () => {
    if (selectedCustomers.length > 0) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers([...data.data]);
    }
  };

  const handleCancel = () => {
    const mergedIds = getSelectedCustomerIds();
    modalActionBody({
      title: `${t('activity:buttons.CANCEL')} ${
        getMode() === 'Assigned' ? 'Unassigned' : 'Assigned'
      } Customers`,
      callBack: () => history.push('/product/feature-flag'),
      text: (
        <Text>
          Are you sure you want to cancel{' '}
          {getMode() === 'Assigned' ? 'unassigned' : 'assigned'} feature:{' '}
          <Text type="s" className={cn(classes.bold, classes.bold_capitalize)}>
            {dataSelectCell.name}
          </Text>{' '}
          to the{' '}
          <Text type="s" className={classes.bold}>
            {mergedIds.length > 0 ? mergedIds.length : 'All Tenants'}
          </Text>
          {mergedIds.length > 0 && ' following customers'}?
        </Text>
      )
    });
  };

  const getSelectedCustomerIds = () => {
    return selectedCustomers.map(c => c.customerId);
  };

  const handleOpenModal = () => {
    const mergedIds = getSelectedCustomerIds();
    modalActionBody({
      title: `${getMode() === 'Assigned' ? 'Unassign' : 'Assign'} ${t(
        'activity:units.CUSTOMERS'
      )}`,
      callBack: () => handlePost(),
      text: (
        <Text>
          You want to {getMode() === 'Assigned' ? 'unassign' : 'assign'} the{' '}
          <Text type="s" className={cn(classes.bold, classes.bold_capitalize)}>
            {dataSelectCell.name}
          </Text>{' '}
          feature to the{' '}
          <Text type="s" className={classes.bold}>
            {mergedIds.length > 0
              ? mergedIds.length
              : `All${testToIsNotFiltered() ? '' : ' filtered'} Tenants`}
          </Text>
          {mergedIds.length > 0 && ' following customers'}?
        </Text>
      ),
      okText: getMode() === 'Assigned' ? 'Unassign' : 'Assign',
      cancelText: t('activity:featureFlag.NO_THANKS')
    });
  };

  const testToIsNotFiltered = () =>
    isEqual(
      { ...filter, ...defaultNavigation, onlyAssigned: true },
      {
        ...defaultFilters,
        ...defaultNavigation,
        onlyAssigned: true
      }
    );

  const handlePost = () => {
    const linkForRequest =
      getMode() === 'Assigned'
        ? '/product/fflag/unassign'
        : '/product/fflag/assign';

    const arrTenantsId = getSelectedCustomerIds();

    let resultData = {
      count: arrTenantsId.length,
      name: dataSelectCell.name,
      mode: getMode() === 'Assigned' ? 'Unassigned' : 'Assigned',
      isNotFiltered: testToIsNotFiltered()
    };
    const REQ_BODY =
      arrTenantsId.length > 0
        ? {
            featureId: dataSelectCell._id,
            customerIds: arrTenantsId
          }
        : {
            featureId: dataSelectCell._id
          };
    (getMode() === 'Assigned'
      ? deleteRequest(linkForRequest, REQ_BODY)
      : putRequest(linkForRequest, REQ_BODY)
    )
      .then(res => {
        if (res?.body?.succeeded !== undefined) {
          resultData = {
            ...resultData,
            succeeded: res.body.succeeded,
            failed: res.body.failed
          };
        }
        if (!unmount.current) {
          history.push('/product/feature-flag');
        }
        showNotif(<ResultAlert data={resultData} />, {
          ms: 10000,
          onBeforeClose: hideNotif
        });
      })
      .catch(() => {
        if (!unmount.current) {
          history.push('/product/feature-flag');
          const errorData = { ...resultData, kind: 'error' };
          showNotif(<ResultAlert data={errorData} />, {
            ms: 10000,
            onBeforeClose: hideNotif
          });
        }
      });
  };

  const handleFilterInput = e => {
    setFilter({ ...filter, search: e.target.value, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.search, value =>
    handleFilterInput({ target: { value } })
  );

  const handleFromTo = e => {
    const target = e.currentTarget;
    const name = target.getAttribute('field');
    if (target.id === name) {
      setFilter({
        ...filter,
        [name]: {
          ...filter[name],
          [`from`]: '',
          [`to`]: ''
        }
      });
      setStartFilter(!startFilter);
    } else {
      if (!validationNumber(target.value)) {
        setFilter({
          ...filter,
          [name]: {
            ...filter[name],
            [target.name]: target.value
          }
        });
        setStartFilter(!startFilter);
      }
    }
  };

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };

  const handleLimitChange = limit => {
    if (filter.itemsPerPage === limit) {
      return;
    }
    setFilter({ ...filter, itemsPerPage: limit, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  const handleSortable = field => () => {
    if (field === filter.fieldName) {
      const directionTemp = filter.direction * -1;
      setFilter({ ...filter, direction: directionTemp });
      setStartFilter(!startFilter);
    } else {
      setFilter({ ...filter, fieldName: field });
      setStartFilter(!startFilter);
    }
  };

  const changeCurrentPage = e => {
    setFilter({ ...filter, currentPage: e });
    setStartFilter(!startFilter);
  };

  // a function to check if the saved filters have a given combination of
  // the selected options and if so, switch to the saved filter

  const onClearSearch = () => {
    setFilter({ ...filter, search: '' });
    setStartFilter(!startFilter);
  };

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = listFilter => {
    setSelectLoader(true);

    getRequest('/product/fflag/customers/list/filters')
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({ ...filters, ...body });
          // we are checking or in the new filter lists there are
          // parameters that are selected, and those that are not, turn off the selection
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          newCheckingList && setPrevStateFilter(newCheckingList);
          newCheckingList &&
            replaceHistory(
              filter,
              undefined,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );
          setSelectLoader(false);
        }
      })
      .catch(() => {
        // error handled silently
      });
  };

  useEffect(() => {
    if (dataSelectCell) {
      //after the select is closed, we get into this function.
      //We check or after closing the filter whether there have
      //been changes in the selected options and if so, we make a request
      if (triggerStartEffect) {
        if (
          (!isEqual(prevStateFilter, filter) &&
            JSON.parse(query.get('filter')) !== null) ||
          !isEqual(
            parsConfigForHistory(prevStateTableConf),
            parsConfigForHistory(tableConf)
          )
        ) {
          replaceHistory(
            filter,
            // setEnabledRequest,
            undefined,
            history,
            false,
            false,
            changeFilter,
            parsConfigForHistory(tableConf)
          );
        }
      }
      setTriggerStartEffect(true);
    }
    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    if (dataSelectCell) {
      // if (enabledRequest) { // were need for mode validation to clear some value in select
      setLoaderTable(true);
      if (
        query.get('filter') === null &&
        isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        replaceHistory(
          filter,
          // setEnabledRequest,
          undefined,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        requestForFilteredData();
      } else if (
        query.get('filter') === null &&
        !isEqual(filter, {
          ...defaultFilters,
          ...defaultNavigation
        })
      ) {
        resetAllfilters(true);
        replaceHistory(
          filter,
          // setEnabledRequest,
          undefined,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      } else {
        setFilter(JSON.parse(query.get('filter')));
        setPrevStateFilter(JSON.parse(query.get('filter')));
        setPrevStateTableConf(
          reParsConfigForHistory(JSON.parse(query.get('tableConf')))
        );
        requestForFilteredData(JSON.parse(query.get('filter')));
        setChangeFilter(query.get('changeFilter'));
        setTableConf(
          reParsConfigForHistory(
            JSON.parse(query.get('tableConf')),
            mainTableConf
          )
        );
      }
      setTrigerResetDatePicker(!trigerResetDatePicker);
      // }
      // setEnabledRequest(true);
    }
    // eslint-disable-next-line
  }, [query.get('filter') || query.get('jarvisRegion')]);

  const requestForFilteredData = (listFilter = filter) => {
    const linkForRequest = `/product/fflag/customers/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;
    const tenantIds = pastedTenantIds.map(i => i.name);
    const REQ_BODY = createReqBody(listFilter, dataSelectCell._id, tenantIds);

    putRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          setData(
            REQ_BODY.filters.onlyAssigned
              ? isNotAssignedCustomers
                ? []
                : res.body
              : res.body
          );
          setDataConection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);

          if (
            tenantIds.length > 0 &&
            !mismatchWarningShown.current &&
            res.body.totalRecords < tenantIds.length
          ) {
            mismatchWarningShown.current = true;
            const found = res.body.totalRecords;
            const missing = tenantIds.length - found;
            modalActionBody({
              title: t('activity:featureFlag.TENANT_ID_MISMATCH_TITLE'),
              callBack: () => {},
              text: (
                <Text>
                  {t('activity:featureFlag.TENANT_ID_MISMATCH_MESSAGE', {
                    pasted: tenantIds.length,
                    found,
                    missing
                  })}
                </Text>
              ),
              okText: t('activity:featureFlag.GOT_IT'),
              cancelText: t('activity:buttons.CANCEL')
            });
          }
        }
      })
      .catch(() => {
        if (!unmount.current) {
          setDataConection(false);
          setLoader(false);
          setLoaderTable(false);
        }
      });
  };

  const resetAllfilters = (
    reload,
    onlyFilterLabel,
    onClickDefault,
    modeClearAll
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    }

    setTrigerResetDatePicker(!trigerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  if (loader) {
    return <Loading />;
  }

  if (!dataConection) {
    return <EmptyState message="connect" />;
  }

  const getMode = () => {
    return JSON.parse(query.get('filter')).onlyAssigned
      ? 'Assigned'
      : 'Not Assigned';
  };

  const setSubTitle = () => {
    return JSON.parse(query.get('filter')).onlyAssigned ? 'Unassign' : 'Assign';
  };

  return (
    <>
      <PageTitle
        title={`${getMode()} ${t('activity:units.CUSTOMERS')}`}
        subTitle={`${setSubTitle()} only for current Active and Non Renewing customers`}
      />
      <TableTitle
        className={classes.featureTitle}
        title={`${t('activity:featureFlag.FEATURE_INFO')}:`}
      />
      {dataSelectCell && (
        <FeatureInfo
          limitCustomersSelect={limitCustomersSelect}
          totalRecords={data.totalRecords}
          data={dataSelectCell}
          isNotFiltered={testToIsNotFiltered()}
          isVisibleButtons={selectedCustomers.length < 1}
          handleCancel={handleCancel}
          handleOpenModal={handleOpenModal}
          action={
            getMode() === 'Assigned'
              ? `Unassign for all${testToIsNotFiltered() ? '' : ' filtered'} `
              : `Assign for all${testToIsNotFiltered() ? '' : ' filtered'}`
          }
          featureId={dataSelectCell._id}
          featureName={dataSelectCell.name}
          autoAssignEnabled={autoAssignEnabled}
          onAutoAssignToggle={() => setAutoAssignEnabled(prev => !prev)}
        />
      )}
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.COMPANY_NAME_ID_PLACEHOLDER')}
          value={filter.search}
          onChange={handleFilterInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable || isTenantIdFilterActive}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <RadioButton
          handleUnit={handleUnit}
          unit={getMode()}
          units={units}
          disabled={loaderTable}
          className={classes.togle}
          classLabel={classes.rBtnlabel}
        />
        {!isTenantIdFilterActive && (
          <SaveFilterBlock
            setFilterButton={setFilterButton}
            visibleFilterSave={visibleFilterSave}
            setVisibleFilterSave={setVisibleFilterSave}
            visibleFilterEdit={visibleFilterEdit}
            setVisibleFilterEdit={setVisibleFilterEdit}
            filter={filter}
            setFilter={setFilter}
            filterBackend={filterBackend}
            setFilterBackend={setFilterBackend}
            changeFilter={changeFilter}
            setChangeFilter={setChangeFilter}
            resetAllfilters={resetAllfilters}
            defaultNavigation={defaultNavigation}
            trigerResetDatePicker={trigerResetDatePicker}
            setTrigerResetDatePicker={setTrigerResetDatePicker}
            startFilter={startFilter}
            setStartFilter={setStartFilter}
            defaultFilters={defaultFilters}
            tableConf={tableConf}
            defaulTableConf={mainTableConf}
            setTableConf={setTableConf}
            triggerSaveTableSettings={triggerSaveTableSettings}
          />
        )}
      </div>
      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        filters={filters}
        filterButton={filterButton || isTenantIdFilterActive}
        trigerResetDatePicker={trigerResetDatePicker}
        resetAllfilters={resetAllfilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
        pastedTenantIds={pastedTenantIds}
        setPastedTenantIds={setPastedTenantIds}
      />
      <div className={classes.actions}>
        <TableTitle
          title={`${getMode()} ${t('activity:units.CUSTOMERS')}`}
          additional={data.totalRecords}
        />
        <div className={classes.actions_right}>
          <TableColumnSetting
            setFilterButton={setFilterButton}
            tableConf={tableConf}
            setTableConf={setTableConf}
            setVisibleFilterSave={setVisibleFilterSave}
            setVisibleFilterEdit={setVisibleFilterEdit}
            changeFilter={changeFilter}
            visibleFilterSave={visibleFilterSave}
            visibleFilterEdit={visibleFilterEdit}
            setTriggerSaveTableSettings={setTriggerSaveTableSettings}
            triggerSaveTableSettings={triggerSaveTableSettings}
            defaulTableConf={mainTableConf}
            position="left"
          />

          <LimitPaginationButton
            currentLimit={filter.itemsPerPage}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
      <div style={paddingForMenuSettings(tableConf, tableHeight)}>
        <div ref={tableHeight}>
          <PageTable
            data={data.data}
            handleSortable={handleSortable}
            loader={loaderTable}
            tableConf={tableConf}
            filter={filter}
            handleFromTo={handleFromTo}
            handleCheckCustomer={handleCheckCustomer}
            isSelectedCustomer={isSelectedCustomer}
            handleChekBox={handleChekBox}
            selectedCustomers={selectedCustomers}
          />
        </div>
        {data.data && loaderTable === false && data.totalPages > 1 ? (
          <PaginationGroup
            pageCount={data.totalPages}
            setPage={changeCurrentPage}
            currentPage={filter.currentPage}
            itemsTotal={data.totalRecords}
            itemsPerPage={filter.itemsPerPage}
          />
        ) : null}
        {selectedCustomers.length > 0 && (
          <FooterModalCustomers
            counter={getSelectedCustomerIds().length}
            handleCancel={handleCancel}
            handleOpenModal={handleOpenModal}
            mode={getMode() === 'Assigned' ? 'Unassign' : 'Assign'}
          />
        )}
      </div>
    </>
  );
};

AssignedCustomers.propTypes = {
  dataSelectCell: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    autoAssign: PropTypes.bool
  }).isRequired,
  isNotAssignedCustomers: PropTypes.bool.isRequired
};

export default AssignedCustomers;
